import React, {
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  NotificationContext,
  OrganisationContext,
  UserContext,
} from "./MainLayout";

/**Services**/
import { getEventsAndNotifications } from "../services/EventsNotifications";

/**Componenets**/
import DropDown from "../utils/components/common/dropdown/dropdown";
import LoadingSpinner from "../utils/components/common/loadingSpinner/LoadingSpinner";
import ModalComponent from "../utils/components/common/modalComponent/ModalComponent";
import Icons from "../utils/components/icons";
import {
  updateWebInfo,
  updateCommonInfo,
} from "../utils/constants/userConstants";
import EventAndNotification from "./EventAndNotification";
import UserProfileAndPreferences from "./UserProfileAndPreferences/UserProfileAndPreferences";

/**Third-Part Components**/
import { Dropdown } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import "antd/dist/antd.css";
import "./EventAndNotification.scss";

const Header = ({ updateSiteDetails }) => {
  const { t } = useTranslation("header");
  /**Contexts**/
  const { userData, userCognitoData, appTheme } = useContext(UserContext);
  const { orgData, siteData } = useContext(OrganisationContext);
  const { isNewNotification, isEvtListedFromDevice, setIsEvtListedFromHeader } =
    useContext(NotificationContext);

  const [isLoading, setIsLoading] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isNotificationListed, setIsNotificationListed] = useState(false);
  const [notificationCount, setNotificationCount] = useState(null);

  const [signoutModal, setSignoutModal] = useState(false);
  const [siteList, setSiteList] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [disabledDropDown, setDisabledDropDown] = useState(false);
  const [handleModalOpen, setHandleModalOpen] = useState(false);

  const location = useLocation();
  const backdrop = useRef();
  const notificationBtn = useRef();

  const handleLogoutClick = useCallback((e) => {
    e.preventDefault();
    setSignoutModal(true);
  }, []);

  const signOut = useCallback(async () => {
    setSignoutModal(false);
    setIsLoading(true);
    try {
      const deviceKey =
        userCognitoData?.signInUserSession?.accessToken?.payload?.device_key;
      const deviceIdArr = [deviceKey];
      const value = {
        deviceLogin: false,
        forceLogout: true,
      };
      await updateCommonInfo(userCognitoData?.username, deviceIdArr, value);
    } catch (error) {}
    setIsLoading(false);
  }, [userCognitoData, userData?.ClientData?.loginInfo]);

  const profileItems = [
    {
      label: (
        <button
          style={{
            background: "none",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
          className="user-preference-icon"
          onClick={() => setHandleModalOpen(true)}
        >
          <>
            {Icons.userPreferencesIcon}
            <span
              className="settingsTitle"
              style={{
                color: "#7159A6",
                fontSize: "20px",
                fontFamily: "Open Sans",
                fontWeight: 600,
              }}
            >
              {t("user_preferences")}
            </span>
          </>
        </button>
      ),
      key: "0",
    },
    {
      label: (
        <a
          href="logout"
          id="logout"
          onClick={handleLogoutClick}
          style={{
            background: "none",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <>
            {Icons.purpleLogoutIcon}
            <span
              className="settingsTitle"
              style={{
                color: "#7159A6",
                fontSize: "20px",
                fontFamily: "Open Sans",
                fontWeight: 600,
              }}
            >
              {t("logout")}
            </span>
          </>
        </a>
      ),
      key: "1",
    },
  ];

  const handleSiteSelection = useCallback(
    async (val) => {
      const selectedOption = siteList.find((item) => item.value === val);
      if (selectedOption) {
        /* 1. Update site details at context level*/
        updateSiteDetails({
          label: selectedOption.label,
          id: val,
          manually: true,
        });
        /* 2. Storing in localStorage to get it on all pages*/
        localStorage.setItem("currentSite", val);
        sessionStorage.setItem("pagination", 0);
        /* 3. update selected site in DB for this device after storing in localStorage*/
        updateWebInfo(userCognitoData?.username);
        setSelectedSite(val);
      }
    },
    [siteList, updateSiteDetails, userCognitoData?.username]
  );

  /**E&N API call for header notification count**/
  const getNotificationList = useCallback(() => {
    const param = {
      userId: userCognitoData?.username,
      siteId: siteData?.id,
      eventType: "All",
      duration: 24,
      size: 500,
      pageNo: 1,
      showEvent: false,
      showUnread: true,
      timeStamp: "DESC",
    };
    getEventsAndNotifications(param)
      .then((response) => {
        if (response?.data?.Items) {
          let count = response?.data?.Items.filter((item) => !item.IsListed);
          count
            ? setIsNotificationListed(false)
            : setIsNotificationListed(true);
          setNotificationCount(count.length);
        }
      })
      .catch((err) => {});
  }, [siteData?.id, userCognitoData?.username]);

  /**Call to getNotificationList**/
  useEffect(() => {
    if (userCognitoData?.username && siteData?.id) {
      getNotificationList();
    }
  }, [
    userCognitoData,
    siteData,
    getNotificationList,
    isNewNotification,
    isEvtListedFromDevice,
  ]);

  /**UseEffect to set dropdown list and default selected value in dropdown**/
  useEffect(() => {
    let siteListArr = [];
    if (!(userData && orgData?.SiteList?.length > 0)) {
      return;
    }
    if (userData.UserRole !== "Super Admin") {
      /**If it's not Super Admin then only display sites that user have access in sites dropdown*/
      orgData.SiteList.forEach((elem, index) => {
        if (elem.HasAccess === "True")
          siteListArr.push({
            label: elem.SiteName,
            value: elem.SiteId,
          });
      });
    } else {
      /**For Super Admin, show all sites in sites dropdown. */
      orgData.SiteList.forEach((elem, index) => {
        siteListArr.push({ label: elem.SiteName, value: elem.SiteId });
      });
    }
    setSiteList(siteListArr);

    let siteStored = localStorage.getItem("currentSite");
    const defaultSelectedOption = siteListArr.find(
      (item) => item.value === siteStored
    );

    /**Set Value for seletec Site in dropdown**/
    if (siteStored && defaultSelectedOption) {
      setSelectedSite(siteStored);
      // const defaultSelectedOption = siteListArr.find(
      //   (item) => item.value === siteStored
      // );
      // Update site details at context level using updateSiteDetails
      updateSiteDetails({
        label: defaultSelectedOption.label,
        id: siteStored,
      });
    } else if (siteListArr && siteListArr.length > 0) {
      setSelectedSite(siteListArr[0].value);
      // Update site details at context level using updateSiteDetails
      updateSiteDetails({
        label: siteListArr[0].label,
        id: siteListArr[0].value,
      });
    }
  }, [orgData?.SiteList, updateSiteDetails, userData, userCognitoData]);

  /**For disabling sites on Sites-Dashboard and USers Dashboard**/
  useEffect(() => {
    if (userData) {
      const path = location.pathname;

      const value =
        userData.UserRole === "Super Admin" &&
        (path === "/sites-dashboard" || path === "/user-management");

      setDisabledDropDown(value);
    }
  }, [location.pathname, userData, disabledDropDown]);

  /**User Preference Modal handler**/
  const handleCheckModalClose = useCallback(() => {
    setHandleModalOpen(false);
  }, []);

  return (
    <>
      <span className="whiteSpaceFixScroll" />
      <nav className="headerWrapper">
        <div className="d-flex align-items-center">
          {orgData?.TenantName && (
            <div className="d-flex align-items-center">
              <span id="tenantname" className="userName">
                {orgData?.TenantName}
              </span>
              {disabledDropDown ? (
                <DropDown
                  options={[
                    ...siteList,
                    { label: t("all_sites"), value: "all_sites" },
                  ]}
                  wrapperClass="headerDropdown"
                  disabled
                  autoid="sitelist"
                  value={selectedSite}
                />
              ) : (
                <DropDown
                  options={siteList}
                  wrapperClass="headerDropdown"
                  autoid="sitelist"
                  value={selectedSite}
                  onSelection={(val) => {
                    handleSiteSelection(val);
                  }}
                />
              )}
            </div>
          )}
        </div>
        <div className="d-flex align-items-center justify-center roleSettingsContainer">
          {/* {userData.UserName && userData?.UserRole && orgData?.TenantName && (
            <span id="username" className="userNameWithRole">
              Welcome {userData?.UserName}{" "}
              <span id="userRole">( {userData?.UserRole} )</span>
            </span>
          )} */}
          {userData.UserName && userData?.UserRole && orgData?.TenantName && (
            <span id="username" className="userNameWithRole">
              Welcome{" "}
              <span
                id="username-text"
                className="truncate-text"
                title={userData?.UserName}
              >
                {userData?.UserName}
              </span>{" "}
              <span id="userRole">( {userData?.UserRole} )</span>
            </span>
          )}

          <div className="notificationPreferencesContainer">
            <button
              type="button"
              title="notifications"
              id="notificationButton"
              onClick={() => {
                setIsEvtListedFromHeader((prev) => !prev);
                setIsNotificationListed(true);
                setIsNotificationOpen(!isNotificationOpen);
              }}
              className="verticalBtn notificationIcon"
              ref={notificationBtn}
            >
              <img
                src={
                  appTheme === "light"
                    ? require("../assets/images/icons/notification.png")
                    : require("../assets/images/icons/dark_notification.png")
                }
                className="img-fluid"
                id="notificationButton"
                alt="notification"
              />
              {!isNotificationListed && notificationCount ? (
                <span>{notificationCount > 9 ? "9+" : notificationCount}</span>
              ) : null}
            </button>
            <Dropdown
              title="Profile"
              menu={{
                items: profileItems,
                id: "userMenuId",
                style: {
                  minWidth: "250px",
                  borderRadius: "20px",
                  boxShadow: " 0px 8px 5px 0px rgba(35, 35, 35, 0.39)",
                  paddingTop: "25px",
                  paddingBottom: "25px",
                  paddingLeft: "10px",
                  paddingRight: "10px",
                  gap: "25px",
                  display: "flex",
                  flexDirection: "column",
                },
              }}
              trigger={["click"]}
              className="userIcon pointer"
              overlayClassName="usermenu"
            >
              <button id="userIconButton">
                <img
                  src={
                    appTheme === "light"
                      ? require("../assets/images/icons/user.png")
                      : require("../assets/images/icons/dark_user.png")
                  }
                  className="img-fluid"
                  alt="user"
                  id="userImage"
                />
              </button>
            </Dropdown>
          </div>
        </div>
        {isNotificationOpen && (
          <div className="notificationDropDownWrapper">
            <Suspense>
              <div className="notificationDropDown d-flex" ref={backdrop}>
                <EventAndNotification
                  buttonRef={notificationBtn}
                  hidePopup={setIsNotificationOpen}
                  setListed={setIsNotificationListed}
                />
              </div>
            </Suspense>
          </div>
        )}
      </nav>
      {/* Loader  starts*/}
      {isLoading && <LoadingSpinner />}
      {/* Loader  Ends */}

      {/* Modal  starts*/}
      <ModalComponent
        idName="logoutmodal"
        isOpen={signoutModal}
        onCloseModal={() => setSignoutModal(false)}
        descriptionText={t("logout_text")}
        needConfirmation={true}
        onConfirm={signOut}
      ></ModalComponent>
      {/* Modal  Ends*/}

      {handleModalOpen ? (
        <UserProfileAndPreferences
          handleCheckModalClose={handleCheckModalClose}
        />
      ) : null}
    </>
  );
};

export default Header;

Header.propTypes = {
  updateSiteDetails: PropTypes.func,
};
